import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import JsonEditor from '../JsonEditor';
import { ChangeRequestCreateRequest, Member } from '@/api/bol/models';

interface MemberWithAccess extends Member {
  disabled?: boolean;
}

interface CreateChangeRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: () => Promise<void>;
  changeRequest: ChangeRequestCreateRequest;
  onChangeRequest: (update: Partial<ChangeRequestCreateRequest>) => void;
  operationsJsonText: string;
  onOperationsChange: (value: string) => void;
  members: MemberWithAccess[];
  membersLoading: boolean;
  selectedMemberIdentifier: string;
  onMemberChange: (identifier: string) => void;
  onRefreshMembers: () => void;
  error: string | null;
  creating: boolean;
}

export const CreateChangeRequestDialog: React.FC<CreateChangeRequestDialogProps> = ({
  open,
  onClose,
  onCreate,
  changeRequest,
  onChangeRequest,
  operationsJsonText,
  onOperationsChange,
  members,
  membersLoading,
  selectedMemberIdentifier,
  onMemberChange,
  onRefreshMembers,
  error,
  creating
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Change Request</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <TextField
          autoFocus
          margin="dense"
          label="Target Revision"
          type="number"
          fullWidth
          variant="outlined"
          value={changeRequest.logisticsObjectRevision}
          onChange={(e) => onChangeRequest({ logisticsObjectRevision: Number.parseInt(e.target.value) || 1 })}
          sx={{ mb: 2 }}
        />
        
        <FormControl fullWidth margin="dense" variant="outlined" sx={{ mb: 2 }}>
          <InputLabel>Requestor Member</InputLabel>
          <Select
            value={selectedMemberIdentifier}
            label="Requestor Member"
            onChange={(e) => onMemberChange(e.target.value)}
            disabled={membersLoading}
            endAdornment={
              <IconButton onClick={onRefreshMembers} disabled={membersLoading} sx={{ mr: 3 }}>
                <Tooltip title="Refresh members">
                  <Refresh />
                </Tooltip>
              </IconButton>
            }
          >
            {membersLoading && (
              <MenuItem disabled>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Loading members...
              </MenuItem>
            )}
            {!membersLoading && members.length === 0 && (
              <MenuItem disabled>No members available</MenuItem>
            )}
            {members.map((member) => (
              <MenuItem
                key={member.identifier}
                value={member.identifier}
                disabled={member.disabled}
                sx={{ opacity: member.disabled ? 0.5 : 1, pointerEvents: member.disabled ? 'none' : 'auto' }}
              >
                {member.identifier}
                {member.disabled && ' (No access)'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <TextField
          margin="dense"
          label="Description"
          multiline
          rows={3}
          fullWidth
          variant="outlined"
          value={changeRequest.description}
          onChange={(e) => onChangeRequest({ description: e.target.value })}
          sx={{ mb: 2 }}
        />
        
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Operations (JSON Patch format)
        </Typography>
        <JsonEditor
          value={operationsJsonText}
          onChange={onOperationsChange}
          height="200px"
          readOnly={false}
        />
        <Typography variant="caption" color="textSecondary">
          {`Example: [{"op": "add", "path": "/fieldName", "value": "newValue"}]`}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">Cancel</Button>
        <Button onClick={onCreate} disabled={creating} variant="contained">
          {creating ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
